import React, { useCallback, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import ConfirmationModal from "../../Common/ConfirmationModal";
import LoadingState from "../../Common/LoadingState";
import { PromptEditorModal } from "../PromptEditorModal";
import { AgentTestLabHeader } from "./AgentTestLabHeader";
import { PromptOptimizerPanel } from "./PromptOptimizerPanel";
import { RunsTab } from "./RunsTab";
import { ScenarioWorkbench } from "./ScenarioWorkbench";
import { TabStrip } from "./TabStrip";
import { TestSettingsDrawer } from "./TestSettingsDrawer";
import { useAgentTestLab } from "./useAgentTestLab";

const AgentTestLab: React.FC = () => {
  const navigate = useNavigate();
  const { uuid: routeUuid } = useParams();
  const [searchParams] = useSearchParams();
  const uuid = routeUuid || searchParams.get("agent") || undefined;
  const onMissingUuid = useCallback(() => navigate("/dashboard"), [navigate]);
  const backTarget = routeUuid && uuid ? `/admin/agents/${uuid}` : "/dashboard";
  const lab = useAgentTestLab({ uuid, onMissingUuid });

  // If the active tab becomes hidden (optimizer goes idle), fall back to scenarios.
  useEffect(() => {
    if (lab.activeTab === "optimizer" && !lab.optimizerVisible) {
      lab.setActiveTab("scenarios");
    }
  }, [lab.activeTab, lab.optimizerVisible, lab]);

  if (lab.loading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <LoadingState message="Loading test lab..." fullPage={true} />
      </div>
    );
  }

  return (
    <>
      <AgentTestLabHeader
        lab={lab}
        onBack={() => navigate(backTarget)}
      />

      <div className="container mx-auto px-4 pt-4 pb-10">
        {(lab.notice || lab.error) && (
          <div className="mb-3 space-y-2">
            {lab.notice && (
              <div className="flex items-start justify-between gap-3 px-3 py-2 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/30">
                <span>{lab.notice}</span>
              </div>
            )}
            {lab.error && (
              <div className="flex items-start justify-between gap-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30">
                <span>{lab.error}</span>
              </div>
            )}
          </div>
        )}

        <TabStrip lab={lab} />

        <div className="pt-4">
          {lab.activeTab === "scenarios" && <ScenarioWorkbench lab={lab} />}
          {lab.activeTab === "runs" && <RunsTab lab={lab} />}
          {lab.activeTab === "optimizer" && <PromptOptimizerPanel lab={lab} />}
        </div>
      </div>

      <TestSettingsDrawer lab={lab} />

      <PromptEditorModal
        isOpen={!!lab.promptEditor}
        prompt={lab.promptEditor?.prompt ?? ""}
        onClose={() => lab.setPromptEditor(null)}
        onSave={(prompt) => {
          void lab.handleSavePromptEdit(prompt);
        }}
      />

      <ConfirmationModal
        isOpen={!!lab.runPendingDelete}
        title="Delete run?"
        message="This run will be hidden from Test Lab history and latest verdicts. Transcripts and scores stay preserved for audit."
        confirmText={lab.deletingRunId ? "Deleting..." : "Delete run"}
        cancelText="Cancel"
        onConfirm={() => {
          void lab.confirmDeleteRun();
        }}
        onCancel={lab.cancelDeleteRun}
        isDestructive
      />
    </>
  );
};

export default AgentTestLab;
